<script>
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import BaseSpinner from '../BaseSpinner/BaseSpinner.svelte';
  import handleFetch from '../../utils/handleFetch';
  import { SPRITESHEET_URL, KEYCODES } from '../../utils/constants';

  let {
    headingText,
    headingLevel = 'h2',
    headingClasses = 'leading-tight font-semibold text-blue-dark-navy text-lg md:text-2xl xl:text-3xl',
    content = undefined,
    emailLabel,
    descriptionLabel,
    reasonLabel,
    reasonOptions,
    emailValidationErrorText,
    successText,
    errorText,
    tryAgainButtonLabel,
    submitButtonText,
    disclaimer,
    lang,
    formType = 'contactUs',
    dataTestId = 'contact-form',
    saasEndpoint = $bindable(process.env.SAAS_CONTACT_ENDPOINT),
  } = $props();

  if (formType === 'partners') {
    saasEndpoint = process.env.SAAS_PARTNER_ENDPOINT;
  }

  // Programatic
  let incomingState = 'initial';
  let textArea = $state();
  let inputValue = $state();
  let inputNode = $state();
  let textAreaValue = $state();
  let selected = $state();

  // Component State Management
  const setState = (state) => {
    currentState = { ...initialStates, [state]: true };
  };

  let currentState = $state();

  const initialStates = {
    initial: false,
    populated: false,
    invalid: false,
    succeeded: false,
    failed: false,
    pending: false,
  };

  setState(incomingState);

  let state = $derived(currentState);

  const checkValidity = () => {
    if (!inputNode.validity.valid || !inputValue) {
      setState('initial');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    checkValidity();
    if (!state.invalid) {
      sendFeedback();
    }
  };

  const sendFeedback = async () => {
    try {
      setState('pending');
      const isValidEmail = inputNode && inputNode.validity.valid && inputValue;
      const response = await handleFetch(`${saasEndpoint}`, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': `${process.env.SAAS_API_KEY}`,
        },
        errorKey: 'Failed to send feedback.',
        body: JSON.stringify({
          description: textAreaValue,
          reason: selected,
          agree: true,
          url: window.location.pathname,
          locale: lang,
          email: isValidEmail ? inputValue : undefined,
        }),
      });

      if (response.ok) {
        setState('succeeded');
      } else {
        setState('failed');
      }
    } catch (err) {
      setState('failed');
      console.error(err); /* eslint-disable-line no-console */
    }
    
    window.umami.track(`${formType}-form-${state.succeeded ? 'success' : 'failed'}`);
  };

  const handleBlur = () => {
    if (!inputNode.validity.valid) {
      if (!inputValue) {
        inputValue = '';
        setState('initial');
      } else {
        setState('invalid');
      }
    }
  };

  const handleRetry = () => {
    setState('initial');
  };

  const handleOnKeydown = (e) => {
    if (e.keyCode === KEYCODES.ESCAPE || e.keyCode === KEYCODES.ENTER) return;
    if (state.invalid) setState('populated');
  };
</script>

<div data-testid={dataTestId} class="max-w-200 mx-auto w-full">
  <BaseHeading className={headingClasses} {headingLevel} dataTestId="{dataTestId}-base-heading">
    {headingText}
  </BaseHeading>
  {#if content}
    <p class="mt-4 md:text-lg">{@html content}</p>
  {/if}
  {#if saasEndpoint}
    {#if state.succeeded}
      <div class="flex flex-col min-w-lg flex-1 items-center justify-center">
        <svg class="w-16 h-16 my-4 fill-current text-blue-cerulean">
          <use xlink:href={`${SPRITESHEET_URL}#check`} />
        </svg>
        <p class="font-semibold text-center md:text-lg mb-4 px-8 text-blue-navy">
          {@html successText}
        </p>
      </div>
    {:else if state.failed}
      <div class="flex flex-col min-w-lg flex-1 items-center justify-center">
        <svg class="w-12 h-12 mb-4 fill-current text-red">
          <use xlink:href={`${SPRITESHEET_URL}#alert`} />
        </svg>
        <p class="font-semibold text-center md:text-lg mb-4 px-8 text-blue-navy">
          {@html errorText}
        </p>
        <div class="flex justify-center items-center mb-8">
          <button
            data-testid="{dataTestId}-try-again-button"
            class="leading-loose text-center text-white font-semibold bg-blue-cerulean hover-hover:hover:bg-blue-darkish active:bg-blue-navy
            focus:outline-hidden focus:shadow-focus active:shadow-active rounded-md whitespace-nowrap min-w-25 px-4 flex items-center
            justify-center h-12 mt-0.5"
            onclick={handleRetry}
          >
            {tryAgainButtonLabel}
          </button>
        </div>
      </div>
    {:else}
      <form
        dataTestId="{dataTestId}-form"
        class="mb-4 mt-6 flex flex-col align-start justify-start"
        onsubmit={handleSubmit}
      >
        <label class="text-sm font-semibold mb-1" for="email">{emailLabel}</label>
        <input
          id="email"
          type="email"
          data-testid="{dataTestId}-email-input"
          class="w-full text-gray-gunmetal rounded border border-gray-gunmetal h-12 px-4 py-2 bg-white text-base
            leading-loose placeholder-gray-bluey focus:outline-hidden focus:shadow-focus active:shadow-active"
          class:border-red={state.invalid}
          class:text-red={state.invalid}
          class:border-gray-gunmetal={state.initial}
          class:text-gray-gunmetal={state.initial}
          readonly={state.pending}
          bind:this={inputNode}
          bind:value={inputValue}
          onblur={handleBlur}
          onkeydown={handleOnKeydown}
          required
          aria-required="true"
        />
        <span
          data-testid="{dataTestId}-validation-message"
          class="text-sm text-red leading-loose"
          class:invisible={!state.invalid}
          class:visible={state.invalid}
        >
          {emailValidationErrorText}
        </span>
        {#if formType === 'contactUs' && reasonLabel && reasonOptions}
          <label class="text-sm font-semibold mb-1" for="reason">{reasonLabel}</label>
          <select
            bind:value={selected}
            disabled={state.pending}
            data-testid="{dataTestId}-reasons"
            id="reason"
            class="w-full text-gray-gunmetal rounded border border-gray-gunmetal px-3 py-2 h-12 bg-white text-base
                leading-loose placeholder-gray-bluey focus:outline-hidden focus:shadow-focus mb-8 active:shadow-active"
          >
            {#each reasonOptions as option, index (index)}
              <option value={option.optionText}>
                {option.optionText}
              </option>
            {/each}
          </select>
        {/if}
        <label class="text-sm font-semibold mb-1" for="description">{descriptionLabel}</label>
        <textarea
          data-testid="{dataTestId}-description-textarea"
          bind:this={textArea}
          bind:value={textAreaValue}
          readonly={state.pending}
          id="description"
          class="w-full text-gray-gunmetal rounded border border-gray-gunmetal px-4 py-2 bg-white text-base
              leading-loose placeholder-gray-bluey focus:outline-hidden focus:shadow-focus active:shadow-active mb-4"
          rows="5"
          maxlength="1024"
          required
          aria-required="true"
        ></textarea>

        {#if disclaimer}
          <div class="markdown-wrapper my-4 text-sm leading-tight text-gray-warm">
            {@html disclaimer.content}
          </div>
        {/if}
        <button
          data-testid="{dataTestId}-button"
          type="submit"
          disabled={state.pending}
          class="shrink-0 leading-loose text-center font-semibold text-white bg-blue-cerulean hover-hover:hover:bg-blue-darkish
              active:bg-blue-navy focus:outline-hidden focus:shadow-focus active:shadow-active rounded-md whitespace-nowrap min-w-25
              px-8 flex items-center justify-center h-16 md:h-12 mx-auto md:mx-0 cursor-pointer w-40"
        >
          {#if state.pending}
            <BaseSpinner dataTestId="{dataTestId}-spinner" />
          {:else}
            {submitButtonText}
          {/if}
        </button>
      </form>
    {/if}
  {/if}
</div>
